import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useMacros } from '../../context/MacrosContext';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const MacroTextBox = ({ 
  value = '', 
  onChange, 
  placeholder = 'Type /m to see available macros or @code to expand...',
  multiline = false,
  rows = 4,
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  disabled = false,
  ...props 
}) => {
  const { macros, loading } = useMacros();
  const [showMacroDialog, setShowMacroDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState(-1);
  const [currentInput, setCurrentInput] = useState('');
  const textFieldRef = useRef(null);
  const rowsPerPage = 15;



  // Filter macros based on search query
  const filteredMacros = useMemo(() => {
    return (macros && Array.isArray(macros)) ? macros.filter(macro => {
      const searchLower = searchQuery.toLowerCase();
      return (
        macro.code?.toLowerCase().includes(searchLower) ||
        macro.expansionText?.toLowerCase().includes(searchLower)
      );
    }) : [];
  }, [macros, searchQuery]);

  // Paginate macros
  const paginatedMacros = useMemo(() => {
    return (filteredMacros && Array.isArray(filteredMacros)) ? filteredMacros.slice(
      currentPage * rowsPerPage,
      currentPage * rowsPerPage + rowsPerPage
    ) : [];
  }, [filteredMacros, currentPage, rowsPerPage]);

  const totalPages = useMemo(() => {
    return (filteredMacros && Array.isArray(filteredMacros)) ? Math.ceil(filteredMacros.length / rowsPerPage) : 0;
  }, [filteredMacros, rowsPerPage]);

  // Handle text input changes
  const handleTextChange = useCallback((event) => {
    const newValue = event.target.value;
    const cursorPos = event.target.selectionStart;
    
    setCurrentInput(newValue);
    setCursorPosition(cursorPos);

    // Check for /m trigger
    const beforeCursor = newValue.substring(0, cursorPos);
    const lastSlashM = beforeCursor.lastIndexOf('/m');
    
    if (lastSlashM !== -1 && lastSlashM < cursorPos) {
      const afterSlashM = beforeCursor.substring(lastSlashM + 2);
      if (afterSlashM.length === 0 || afterSlashM.startsWith(' ')) {
        setTriggerPosition(lastSlashM);
        setSearchQuery('');
        setCurrentPage(0);
        setShowMacroDialog(true);
      }
    }

    // Check for @code pattern
    const atPattern = /@(\w+)/g;
    let match;
    let newText = newValue;
    
    if (macros && Array.isArray(macros)) {
      while ((match = atPattern.exec(newValue)) !== null) {
        const code = match[1];
        const macro = macros.find(m => m.code === code);
        if (macro) {
          newText = newText.replace(match[0], macro.expansionText);
        }
      }
    }

    if (newText !== newValue) {
      onChange({ target: { value: newText } });
    } else {
      onChange(event);
    }
  }, [macros, onChange]);

  // Handle macro selection
  const handleMacroSelect = useCallback((macro) => {
    if (triggerPosition !== -1) {
      const beforeTrigger = currentInput.substring(0, triggerPosition);
      const afterCursor = currentInput.substring(cursorPosition);
      const newValue = beforeTrigger + macro.expansionText + afterCursor;
      
      onChange({ target: { value: newValue } });
      setCurrentInput(newValue);
    }
    
    setShowMacroDialog(false);
    setTriggerPosition(-1);
    setSearchQuery('');
    setCurrentPage(0);
  }, [triggerPosition, currentInput, cursorPosition, onChange]);

  // Handle key events
  const handleKeyDown = useCallback((event) => {
    if (showMacroDialog) {
      if (event.key === 'Escape') {
        setShowMacroDialog(false);
        setTriggerPosition(-1);
        event.preventDefault();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        // Select first macro if available
        if (paginatedMacros && Array.isArray(paginatedMacros) && paginatedMacros.length > 0) {
          handleMacroSelect(paginatedMacros[0]);
        }
        event.preventDefault();
      }
    } else {
      // Handle @code expansion on Tab or Enter
      if (event.key === 'Tab' || event.key === 'Enter') {
        const beforeCursor = currentInput.substring(0, cursorPosition);
        const atMatch = beforeCursor.match(/@\w+$/);
        
        if (atMatch) {
          const code = atMatch[1];
          if (macros && Array.isArray(macros)) {
            const macro = macros.find(m => m.code === code);
            if (macro) {
              const beforeAt = currentInput.substring(0, beforeCursor.lastIndexOf('@'));
              const afterCursor = currentInput.substring(cursorPosition);
              const newValue = beforeAt + macro.expansionText + afterCursor;
              
              onChange({ target: { value: newValue } });
              setCurrentInput(newValue);
              event.preventDefault();
            }
          }
        }
      }
    }
  }, [showMacroDialog, paginatedMacros, currentInput, cursorPosition, macros, onChange, handleMacroSelect]);

  // Handle search in macro dialog
  const handleMacroSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
  };

  // Handle page change in macro dialog
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <>
      <TextField
        ref={textFieldRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        fullWidth={fullWidth}
        variant={variant}
        size={size}
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="text.secondary">
                /m for macros
              </Typography>
            </InputAdornment>
          ),
        }}
        {...props}
      />

      {/* Macro Selection Dialog */}
      <Dialog 
        open={showMacroDialog} 
        onClose={() => setShowMacroDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '70vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Select Macro</Typography>
            <Chip 
              label={`${(filteredMacros && Array.isArray(filteredMacros)) ? filteredMacros.length : 0} macros`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search macros..."
            value={searchQuery}
            onChange={handleMacroSearch}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {(paginatedMacros && Array.isArray(paginatedMacros)) ? paginatedMacros.map((macro) => (
                  <ListItem key={macro.id} disablePadding>
                    <ListItemButton onClick={() => handleMacroSelect(macro)}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={macro.code} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            <Typography variant="body2" color="text.secondary">
                              {macro.expansionText.length > 50 
                                ? `${macro.expansionText.substring(0, 50)}...` 
                                : macro.expansionText
                              }
                            </Typography>
                          </Box>
                        }
                        secondary={
                          macro.expansionText.length > 50 && (
                            <Typography variant="caption" color="text.secondary">
                              {macro.expansionText}
                            </Typography>
                          )
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )) : null}
              </List>
              
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2} gap={1}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Chip
                      key={i}
                      label={i + 1}
                      size="small"
                      color={currentPage === i ? "primary" : "default"}
                      onClick={() => handlePageChange(i)}
                      clickable
                    />
                  ))}
                </Box>
              )}
              
              {(paginatedMacros && Array.isArray(paginatedMacros) && paginatedMacros.length === 0) && (
                <Box textAlign="center" p={3}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No macros found matching your search.' : 'No macros available.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MacroTextBox; 